// Feature-specific colors for conditional formatting cell highlights.
// Color keys map 1:1 to backend API color keys.
// Actual hex values come from the theme system (ruleGreenColor, etc.)
export enum ColorOption {
    GREEN = "GREEN",
    YELLOW = "YELLOW",
    BLUE = "BLUE",
    RED = "RED",
    PURPLE = "PURPLE",
    ORANGE = "ORANGE",
    PINK = "PINK",
    CYAN = "CYAN",
    GRAY = "GREY",
}

export const COLOR_OPTIONS = [
    { translationKey: "ConditionalFormattingRules.colors.green", value: ColorOption.GREEN, apiKey: "GREEN" },
    { translationKey: "ConditionalFormattingRules.colors.yellow", value: ColorOption.YELLOW, apiKey: "YELLOW" },
    { translationKey: "ConditionalFormattingRules.colors.blue", value: ColorOption.BLUE, apiKey: "BLUE" },
    { translationKey: "ConditionalFormattingRules.colors.red", value: ColorOption.RED, apiKey: "RED" },
    { translationKey: "ConditionalFormattingRules.colors.purple", value: ColorOption.PURPLE, apiKey: "PURPLE" },
    { translationKey: "ConditionalFormattingRules.colors.orange", value: ColorOption.ORANGE, apiKey: "ORANGE" },
    { translationKey: "ConditionalFormattingRules.colors.pink", value: ColorOption.PINK, apiKey: "PINK" },
    { translationKey: "ConditionalFormattingRules.colors.cyan", value: ColorOption.CYAN, apiKey: "CYAN" },
    { translationKey: "ConditionalFormattingRules.colors.gray", value: ColorOption.GRAY, apiKey: "GREY" },
];

export function getColorHexFromTheme(
    colorKey: string,
    theme: {
        ruleGreenColor: string;
        ruleYellowColor: string;
        ruleBlueColor: string;
        ruleRedColor: string;
        rulePurpleColor: string;
        ruleOrangeColor: string;
        rulePinkColor: string;
        ruleCyanColor: string;
        ruleGrayColor: string;
    }
): string {
    const colorMap: Record<string, string> = {
        GREEN: theme.ruleGreenColor,
        YELLOW: theme.ruleYellowColor,
        BLUE: theme.ruleBlueColor,
        RED: theme.ruleRedColor,
        PURPLE: theme.rulePurpleColor,
        ORANGE: theme.ruleOrangeColor,
        PINK: theme.rulePinkColor,
        CYAN: theme.ruleCyanColor,
        GREY: theme.ruleGrayColor,
    };
    return colorMap[colorKey];
}

export const CONDITION_OPTIONS = [
    { translationKey: "ConditionalFormattingRules.conditions.equals", apiKey: "EQUAL" },
    { translationKey: "ConditionalFormattingRules.conditions.notContains", apiKey: "NOT_CONTAINS" },
    { translationKey: "ConditionalFormattingRules.conditions.contains", apiKey: "CONTAINS" },
];

export function colorHexToApiKey(colorKey: string): string {
    return COLOR_OPTIONS.find((c) => c.value === colorKey)?.apiKey ?? colorKey;
}

export function apiKeyToColorHex(apiKey: string): string {
    return COLOR_OPTIONS.find((c) => c.apiKey === apiKey)?.value ?? apiKey;
}

export function conditionToApi(translationKey: string): string {
    return CONDITION_OPTIONS.find((c) => c.translationKey === translationKey)?.apiKey ?? translationKey;
}

export function apiToCondition(apiKey: string): string {
    return CONDITION_OPTIONS.find((c) => c.apiKey === apiKey)?.translationKey ?? apiKey;
}

export interface ConditionalRule {
    color: string;
    value: string;
    enabled: boolean;
    operator: string;
    order?: number;
    priority?: number;
}

export interface ConditionalRuleSet {
    ruleSet: Record<string, ConditionalRule[]>;
}

export interface ConditionalRuleData {
    tenantUuid: string;
    ruleUuid: string;
    ruleSet: Record<string, ConditionalRule[]>;
    updatedBy?: string;
    createdDate?: string;
    updatedDate?: string;
}

function evaluateCondition(operator: string, cellValue: string, ruleValue: string): boolean {
    const normalizedCell = cellValue.toLowerCase();
    const normalizedRule = ruleValue.toLowerCase();
    switch (operator) {
        case "EQUAL":
            return normalizedCell === normalizedRule;
        case "CONTAINS":
            return normalizedCell.includes(normalizedRule);
        case "NOT_CONTAINS":
            return !normalizedCell.includes(normalizedRule);
        default:
            return false;
    }
}

export function getConditionalCellColorKey(
    rules: ConditionalRuleData[],
    columnApiKey: string,
    cellValue: string | undefined
): string | undefined {
    if (!cellValue || rules.length === 0) {
        return undefined;
    }

    for (const ruleData of rules) {
        const columnRules = ruleData.ruleSet[columnApiKey];
        if (!columnRules) {
            continue;
        }

        const enabledRules = columnRules
            .filter((rule) => rule.enabled)
            .sort((a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity));

        for (const rule of enabledRules) {
            if (evaluateCondition(rule.operator, cellValue, rule.value)) {
                return rule.color;
            }
        }
    }

    return undefined;
}
